import { GcliRateGroup, RateGroup } from "../../features/actuarial/rates-state";


type Rate1865Item = {
    type: 'plan' | 'rider';
    amount: string;
    name: string;
    plan_id?: number | null;
    rider_id?: number | null;
    acronym?: string;
};

type Rate1865Group = {
    title: string;
    data: Rate1865Item[];
};

export function validate1865MonthConsistency(rates: Rate1865Group[]): string | null {
    const numericPattern = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;

    const getCategory = (amount: string): string | null => {
        const normalizedAmount = amount.trim().toUpperCase();

        if (normalizedAmount === 'N/A' || normalizedAmount === 'FREE') {
            return normalizedAmount;
        }

        const normalizedNumericAmount = normalizedAmount.replace(/,/g, '');

        if (!numericPattern.test(normalizedNumericAmount)) {
            return null;
        }

        const decimalPart = normalizedNumericAmount.split('.')[1];

        if (decimalPart && decimalPart.length > 4) {
            return null;
        }

        return 'NUMERIC';
    };

    // Validate each Plan and Rider independently in every month.
    for (const month of rates ?? []) {
        for (const item of month.data ?? []) {
            const amount = item.amount?.trim() ?? '';

            if (!amount) {
                return `${month.title}: "${item.name}" has an empty rate.`;
            }

            const normalizedAmount = amount.toUpperCase();
            const normalizedNumericAmount = amount.replace(/,/g, '');

            if (item.type === 'plan') {
                // Plans accept N/A or numeric values with up to 4 decimal places.
                if (normalizedAmount === 'N/A') {
                    continue;
                }

                if (!numericPattern.test(normalizedNumericAmount)) {
                    return `${month.title}: "${item.name}" must be numeric or N/A.`;
                }

                if (normalizedNumericAmount.split('.')[1]?.length > 4) {
                    return `${month.title}: "${item.name}" cannot have more than 4 decimal places.`;
                }

                continue;
            }

            // Riders accept N/A, FREE, or numeric values.
            if (getCategory(normalizedAmount) === null) {
                if (
                    numericPattern.test(normalizedNumericAmount) &&
                    normalizedNumericAmount.split('.')[1]?.length > 4
                ) {
                    return `${month.title}: "${item.name}" cannot have more than 4 decimal places.`;
                }

                return `${month.title}: "${item.name}" must be numeric, FREE, or N/A.`;
            }
        }
    }

    // Check consistency across months for the same Plan or Rider.
    const previousValues = new Map<
        string,
        { category: string; month: string; name: string }
    >();

    for (const month of rates ?? []) {
        for (const item of month.data ?? []) {
            const amount = item.amount.trim().toUpperCase();
            const category = getCategory(amount);

            if (category === null) continue;

            const itemId =
                item.type === 'plan'
                    ? item.plan_id ?? item.name
                    : item.rider_id ?? item.name;

            const key = `${item.type}:${itemId}`;
            const previous = previousValues.get(key);

            if (previous && previous.category !== category) {
                const label = item.acronym || item.name;

                return `"${label}" must keep the same value type across loan terms. It is ${previous.category === 'NUMERIC' ? 'numeric' : previous.category} in ${previous.month}, but ${category === 'NUMERIC' ? 'numeric' : category} in ${month.title}.`;
            }

            if (!previous) {
                previousValues.set(key, {
                    category,
                    month: month.title,
                    name: item.name,
                });
            }
        }
    }

    return null;
}



type GyrtGpaRateItem = {
    type: 'plan' | 'rider';
    amount: string;
    name: string;
};

type GyrtGpaRateGroup = {
    title: string;
    data: GyrtGpaRateItem[];
};


export function validateGYRTGPARates(rates: RateGroup[]): string | null {
    const numericPattern = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;

    const isValidNumber = (value: string): boolean => {
        const normalizedValue = value.replace(/,/g, '');

        if (!numericPattern.test(normalizedValue)) return false;

        const decimalPart = normalizedValue.split('.')[1];

        return !decimalPart || decimalPart.length <= 4;
    };

    for (const group of rates ?? []) {
        const plan = group.data?.find((item) => item.type === 'plan');

        if (!plan) {
            return `${group.title}: product data is missing.`;
        }

        const planAmount = plan.amount?.trim() ?? '';

        // // Plan: required, numeric only, maximum of 4 decimal places.
        // if (!planAmount) {
        //     return `${group.title}: "${plan.name}" has an empty rate.`;
        // }

        // if (!isValidNumber(planAmount)) {
        //     if (planAmount.replace(/,/g, '').match(numericPattern)) {
        //         return `${group.title}: "${plan.name}" cannot have more than 4 decimal places.`;
        //     }

        //     return `${group.title}: "${plan.name}" must be a valid numeric rate.`;
        // }
        // Plan: required, N/A or numeric, maximum of 4 decimal places.
        if (!planAmount) {
            return `${group.title}: "${plan.name}" has an empty rate.`;
        }

        if (planAmount.toUpperCase() !== 'N/A' && !isValidNumber(planAmount)) {
            if (planAmount.replace(/,/g, '').match(numericPattern)) {
                return `${group.title}: "${plan.name}" cannot have more than 4 decimal places.`;
            }

            return `${group.title}: "${plan.name}" must be a valid numeric rate or N/A.`;
        }

        // Rider: required, and must be N/A, FREE, or numeric.
        for (const rider of group.data.filter((item) => item.type === 'rider')) {
            const amount = rider.amount?.trim() ?? '';
            const normalizedAmount = amount.toUpperCase();

            if (!amount) {
                return `${group.title}: "${rider.name}" has an empty rate.`;
            }

            if (['N/A', 'FREE'].includes(normalizedAmount)) {
                continue;
            }

            if (!isValidNumber(amount)) {
                if (amount.replace(/,/g, '').match(numericPattern)) {
                    return `${group.title}: "${rider.name}" cannot have more than 4 decimal places.`;
                }

                return `${group.title}: "${rider.name}" must be a valid numeric rate, FREE, or N/A.`;
            }
        }
    }

    return null;
}








export function validate6680MonthConsistency(rates: GcliRateGroup[]): string | null {
    const numericPattern = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;
    const monthValues = new Map<string, { category: string; age: string }>();

    for (const ageGroup of rates ?? []) {
        for (const item of ageGroup.data ?? []) {
            const amount = item.amount?.trim() ?? '';
            const normalizedAmount = amount.toUpperCase();

            if (!amount) {
                return `Age ${ageGroup.title}, "${item.name}" has an empty rate.`;
            }

            let category: string;

            if (normalizedAmount === 'N/A') {
                category = 'N/A';
            } else {
                const normalizedNumericAmount = amount.replace(/,/g, '');

                if (!numericPattern.test(normalizedNumericAmount)) {
                    return `Age ${ageGroup.title}, "${item.name}" must be numeric or N/A.`;
                }

                if (normalizedNumericAmount.split('.')[1]?.length > 4) {
                    return `Age ${ageGroup.title}, "${item.name}" cannot have more than 4 decimal places.`;
                }

                category = 'NUMERIC';
            }

            // Identify the same rider or plan across all ages.
            const itemId = item.type === 'plan'
                ? item.plan_id ?? item.name
                : item.rider_id ?? item.name;

            const key = `${item.type}:${itemId}`;
            const previous = monthValues.get(key);

            if (previous && previous.category !== category) {
                return `"${item.name}" must use the same value type across ages ${previous.age} and ${ageGroup.title}.`;
            }

            if (!previous) {
                monthValues.set(key, {
                    category,
                    age: ageGroup.title,
                });
            }
        }
    }

    return null;
}






export function validate6680AgeConsistency(rates: GcliRateGroup[]): string | null {
    const numericPattern = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;
    const previousValues = new Map<string, { category: string; age: string; name: string }>();

    for (const ageGroup of rates ?? []) {
        const plan = ageGroup.data?.find((item) => item.type === 'plan');

        if (!plan) {
            return `Age ${ageGroup.title}: plan data is missing.`;
        }

        for (const item of ageGroup.data ?? []) {
            const amount = item.amount?.trim() ?? '';
            const normalizedAmount = amount.toUpperCase();

            if (!amount) {
                return `Age ${ageGroup.title}, "${item.name}" has an empty rate.`;
            }

            let category: string;
            const normalizedNumericAmount = amount.replace(/,/g, '');

            if (item.type === 'plan') {
                // Plan: numeric only, with a maximum of 4 decimal places.
                if (!numericPattern.test(normalizedNumericAmount)) {
                    return `Age ${ageGroup.title}, "${item.name}" must be numeric.`;
                }

                if (normalizedNumericAmount.split('.')[1]?.length > 4) {
                    return `Age ${ageGroup.title}, "${item.name}" cannot have more than 4 decimal places.`;
                }

                category = 'NUMERIC';
            } else {
                // Rider: numeric, FREE, or N/A, with a maximum of 4 decimal places.
                if (normalizedAmount === 'N/A' || normalizedAmount === 'FREE') {
                    category = normalizedAmount;
                } else {
                    if (!numericPattern.test(normalizedNumericAmount)) {
                        return `Age ${ageGroup.title}, "${item.name}" must be numeric, FREE, or N/A.`;
                    }

                    if (normalizedNumericAmount.split('.')[1]?.length > 4) {
                        return `Age ${ageGroup.title}, "${item.name}" cannot have more than 4 decimal places.`;
                    }

                    category = 'NUMERIC';
                }
            }

            const itemId = item.type === 'plan'
                ? item.plan_id ?? item.name
                : item.rider_id ?? item.name;

            const key = `${item.type}:${itemId}`;
            const previous = previousValues.get(key);

            if (previous && previous.category !== category) {
                const label = item.acronym || item.name;
                const previousCategory = previous.category === 'NUMERIC' ? 'a number' : previous.category;
                const currentCategory = category === 'NUMERIC' ? 'a number' : category;

                return `"${label}" must keep the same value type across ages. It is ${previousCategory} at age ${previous.age}, but ${currentCategory} at age ${ageGroup.title}.`;
            }

            if (!previous) {
                previousValues.set(key, {
                    category,
                    age: ageGroup.title,
                    name: item.name,
                });
            }
        }
    }

    return null;
}


